import { firstValueFrom } from 'rxjs'
import type { FileForProcessing, IOrder, IPrimer, ISamplePool, IUser } from '../../Model/index.ts'
import type { ValidationIssue } from '../../Model/Order/ValidationIssue.ts'
import {
  getDetectedSequencingPlatform,
  getEffectiveSequencingPlatform,
  getPlatformProcessingTypeConflict
} from '../../Util/sequencingPlatform.ts'
import {
  MismatchingAmpliconError,
  PrimerNotFoundError,
  validatePrimers
} from '../../View/Page/OrderManagementPage/OrderDetail/PrimerValidation.tsx'
import type { SubmitBlockedReason } from '../Telemetry/OrderTelemetry.ts'
import { trackSubmitBlocked } from '../Telemetry/OrderTelemetry.ts'
import type * as OrderRepositoryModule from '../../Repository/Order/OrderRepository.ts'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.ts'
import { ProcessingTypes } from '../../Generated/ProcessingType.ts'
import type { SubmitReview } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { OrderSubmissionPreview } from '../../Generated/OrderSubmissionPreview.ts'
import type { LengthCheck } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { ReadLengthSummary } from '../../Util/orderSettingDetection/readLengths.ts'
import { checkReadLengths, measuredWindowFor } from './lengthCheck.ts'

export type SubmitReadiness =
  | { ok: true; primer: IPrimer | 'SKIPPED'; updates: FileForProcessing[] }
  | { ok: false; reason: 'not-lab' | 'platform' | 'amplicon-mismatch' | 'primer-not-found' | 'validation'; message: string }

const TRIMMED = ['TRIMMED_SAMPLES', 'MERGED_AND_TRIMMED_SAMPLES']

/* an HTTP client's own message ("Request failed with status code 422") means nothing to a lab */
const safeMessage = (error: unknown) => {
  const status = (error as { response?: { status?: number }; status?: number } | null)?.response?.status ?? (error as { status?: number } | null)?.status
  if (typeof status === 'number') {
    return `the primer check could not be completed (server answered ${status})`
  }
  return error instanceof Error ? error.message : String(error)
}

const refused = (
  reason: Exclude<SubmitReadiness, { ok: true }>['reason'],
  message: string,
  blocked: SubmitBlockedReason | null,
  order: IOrder
): SubmitReadiness => {
  if (blocked) {
    trackSubmitBlocked(blocked, order)
  }
  return { ok: false, reason, message }
}

const fromValidationError = (error: unknown, order: IOrder, track = true): SubmitReadiness => {
  if (error instanceof MismatchingAmpliconError) {
    return refused(
      'amplicon-mismatch',
      `Primer is registered for a different amplicon. ${error.message}`,
      track ? 'amplicon_mismatch' : null,
      order
    )
  }
  if (error instanceof PrimerNotFoundError) {
    return refused('primer-not-found', `Primer not found! ${error.message}`, track ? 'primer_not_found' : null, order)
  }
  return refused('validation', `Sample validation failed! ${safeMessage(error)}`, track ? 'validation_error' : null, order)
}

const platformOf = (order: IOrder, pools: ISamplePool[]) =>
  getEffectiveSequencingPlatform(getDetectedSequencingPlatform(order, pools), order.sequencingPlatformOverride)

const allItems = (pools: ISamplePool[]) => pools.flatMap((pool) => [...(pool.samples ?? []), ...(pool.negativeControls ?? [])])

const unsetMin = (item: FileForProcessing) => item.minBasePairLength === null || item.minBasePairLength === undefined

export type SubmissionOptions = {
  track?: boolean
  /** The read lengths the wizard sampled from the dropped files. */
  readLengths?: ReadLengthSummary
}

/**
 * The classic submit button's checks, in its order (OrderContextActions.validatePrimersAndShowSubmit):
 * only the processing lab submits; the platform must allow the processing type; primers are validated
 * against the registry — mandatory for untrimmed formats, and for trimmed formats whenever any primer
 * was given. Returns the items to update with the primer's length settings, as the classic flow does;
 * trimmed ONT reads get the length window measured on them instead.
 */
export const prepareSubmission = async (
  order: IOrder,
  pools: ISamplePool[],
  user: IUser | undefined,
  validate: typeof validatePrimers = validatePrimers,
  { track = true, readLengths }: SubmissionOptions = {}
): Promise<SubmitReadiness> => {
  const refuse = (reason: Exclude<SubmitReadiness, { ok: true }>['reason'], message: string, blocked: SubmitBlockedReason | null) =>
    refused(reason, message, track ? blocked : null, order)
  if (!user || user.org.id !== order.lab) {
    return refuse('not-lab', 'Only the processing lab can submit this order to the pipeline.', 'not_lab')
  }
  const conflict = getPlatformProcessingTypeConflict({
    detectedSequencingPlatform: getDetectedSequencingPlatform(order, pools),
    sequencingPlatformOverride: order.sequencingPlatformOverride,
    processingType: order.processingType
  })
  if (conflict) {
    return refuse('platform', conflict, null)
  }
  if (!TRIMMED.includes(order.sampleConfiguration)) {
    try {
      const [updatedPools, primer] = await validate(order, pools)
      if (!primer) {
        return refuse(
          'validation',
          pools[0] && (pools[0].samples ?? []).length === 0
            ? 'The primers are checked on the first pool’s first sample, and the first pool holds only negative controls: move a sample into it.'
            : 'The primers could not be checked.',
          'primer_null'
        )
      }
      return { ok: true, primer, updates: updatedPools.flatMap((pool) => [...pool.samples, ...pool.negativeControls]) }
    } catch (error) {
      return fromValidationError(error, order, track)
    }
  }
  const measured = measuredWindowFor(platformOf(order, pools), order.sampleConfiguration, readLengths)
  const withMeasured = (updates: FileForProcessing[]) =>
    measured ? updates.map((item) => ({ ...item, minBasePairLength: measured.min, maxBasePairLength: measured.max })) : updates
  const unvalidated = (): SubmitReadiness => ({
    ok: true,
    primer: 'SKIPPED',
    updates: measured ? withMeasured(allItems(pools).filter(unsetMin)) : []
  })
  const anyPrimerProvided = allItems(pools).some((item) => !!item.forwardPrimer || !!item.reversePrimer)
  /* nothing to validate: the registry lookup would only be refused, and the review already says validation is skipped */
  if (!anyPrimerProvided) {
    return unvalidated()
  }
  try {
    const [updatedPools, primer] = await validate(order, pools)
    if (!primer) {
      return unvalidated()
    }
    const original = new Map(allItems(pools).map((item) => [item.id, item]))
    /* items that already carry a min length keep it: it may be the customer's */
    const updates = allItems(updatedPools).filter((item) => {
      const before = original.get(item.id)
      return !before || unsetMin(before)
    })
    return { ok: true, primer, updates: withMeasured(updates) }
  } catch (error) {
    return fromValidationError(error, order, track)
  }
}

/** `prepareSubmission` as a check only: nothing is written and nothing is reported as a blocked submit. */
export const dryRunSubmission = (
  order: IOrder,
  pools: ISamplePool[],
  user: IUser | undefined,
  validate: typeof validatePrimers = validatePrimers,
  readLengths?: ReadLengthSummary
): Promise<SubmitReadiness> => prepareSubmission(order, pools, user, validate, { track: false, readLengths })

/** The review's read-length check against the items as submit will leave them. */
export const lengthCheckFor = (
  order: IOrder,
  pools: ISamplePool[],
  readiness: Extract<SubmitReadiness, { ok: true }>,
  readLengths: ReadLengthSummary | undefined
): LengthCheck | undefined => {
  const updated = new Map(readiness.updates.map((item) => [item.id, item]))
  return checkReadLengths({
    lengths: readLengths,
    order,
    platform: platformOf(order, pools),
    primer: readiness.primer,
    items: allItems(pools).map((item) => updated.get(item.id) ?? item)
  })
}

/** Writes the primer's length settings per pool, as the classic flow does before opening its submit modal. */
export const applySubmitUpdates = async (
  order: IOrder,
  updates: FileForProcessing[],
  repository: Pick<typeof OrderRepositoryModule, 'updatePoolContent'>
) => {
  const byPool = new Map<string, FileForProcessing[]>()
  for (const item of updates) {
    if (item.poolId) {
      byPool.set(item.poolId, [...(byPool.get(item.poolId) ?? []), item])
    }
  }
  try {
    await Promise.all([...byPool].map(([poolId, items]) => firstValueFrom(repository.updatePoolContent(order.id, poolId, items))))
  } catch (error) {
    trackSubmitBlocked('pool_update_failed', order)
    throw error
  }
}

/* keep in sync with the backend's OrderSubmissionEventListener.submissionEventsItems */
const awaitsProcessing = (item: FileForProcessing): boolean => {
  const info = item.pipelineInformation
  if (!info || info.errorAt || !info.submittedAt || !info.completedAt) {
    return true
  }
  const version = item.version ? Date.parse(item.version) : Number.NEGATIVE_INFINITY
  return version > Date.parse(info.submittedAt) || version > Date.parse(info.completedAt)
}

/**
 * The submission preview as it will stand once submit has written `updates`: a written item gets a new
 * version, so one already processed is sent to preprocessing again.
 */
export const previewAfterUpdates = (
  preview: OrderSubmissionPreview | undefined,
  pools: ISamplePool[],
  updates: FileForProcessing[]
): OrderSubmissionPreview | undefined => {
  if (!preview) {
    return preview
  }
  const items = new Map(pools.flatMap((pool) => [...(pool.samples ?? []), ...(pool.negativeControls ?? [])]).map((item) => [item.id, item]))
  const rerun = new Set(
    updates.map((update) => items.get(update.id)).filter((item): item is FileForProcessing => !!item && !awaitsProcessing(item))
  ).size
  if (rerun === 0) {
    return preview
  }
  return {
    ...preview,
    toProcess: Math.min(preview.total, preview.toProcess + rerun),
    alreadyProcessed: Math.max(0, preview.alreadyProcessed - rerun)
  }
}

/** A rejected submit's validation issues as short lines; the classic view has the full explanations. */
export const describeSubmitIssues = (error: unknown): string[] => {
  const body = (error as { response?: unknown } | null)?.response
  if (!Array.isArray(body)) {
    return [safeMessage(error)]
  }
  return (body as ValidationIssue[]).map((issue) => {
    const text = 'issue' in issue && typeof issue.issue === 'string' ? issue.issue : ''
    const sample =
      'item' in issue && issue.item && typeof issue.item === 'object' ? (issue.item as FileForProcessing).customerSampleName : undefined
    return [issue.type.replaceAll('-', ' '), sample, text].filter(Boolean).join(': ')
  })
}

type SubmitRepository = Pick<typeof OrderRepositoryModule, 'getOrder' | 'getAllSamplePools' | 'updatePoolContent' | 'submitOrder'>

/**
 * Submits the order the way the classic flow does, re-running its checks on the order as it is now:
 * anything may have changed since the review was shown.
 */
export const submitWithChecks = async (
  orderId: string,
  user: IUser | undefined,
  repository: SubmitRepository,
  validate: typeof validatePrimers = validatePrimers,
  readLengths?: ReadLengthSummary
): Promise<SubmitReadiness> => {
  const order = await firstValueFrom(repository.getOrder(orderId))
  const pools = await firstValueFrom(repository.getAllSamplePools({ orderId }))
  const readiness = await prepareSubmission(order, pools, user, validate, { readLengths })
  if (!readiness.ok) {
    return readiness
  }
  await applySubmitUpdates(order, readiness.updates, repository)
  await firstValueFrom(repository.submitOrder({ orderId }, user))
  return readiness
}

/**
 * The classic platform override, sent with the order's version as it is now rather than as the review
 * read it: the wizard or the classic view may have written the order since.
 */
export const overridePlatform = async (
  orderId: string,
  platform: SequencingPlatformKey | null,
  repository: Pick<typeof OrderRepositoryModule, 'getOrder' | 'overrideSequencingPlatform'>
): Promise<IOrder> => {
  const current = await firstValueFrom(repository.getOrder(orderId))
  return firstValueFrom(repository.overrideSequencingPlatform({ orderId, platform, version: current.version || '0' }))
}

export const reviewSamples = (review: Pick<SubmitReview, 'pools'>) => review.pools.flatMap((pool) => pool.samples ?? [])

/** Whether a sample name is used more than once among the samples submitted. */
export const duplicateNames = (review: Pick<SubmitReview, 'pools'>): boolean => {
  const samples = reviewSamples(review)
  return samples.length !== new Set(samples.map((sample) => sample.customerSampleName)).size
}

/** Whether the amplicon min/max length is the lab's to adjust: trimmed samples, or eDNA with validated primers. */
export const trimmedOrEdna = ({ order, primer }: Pick<SubmitReview, 'order' | 'primer'>): boolean =>
  TRIMMED.includes(order.sampleConfiguration) || (order.processingType === ProcessingTypes.EDNA.key && primer !== 'SKIPPED')

export { reviewSignature } from './reviewSignature.ts'
